import { TextField, outlinedInputClasses, buttonBaseClasses, useTheme, touchRippleClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import React, { useState, useRef, useEffect, useContext } from "react";
import Tooltip from "@hyro-components/Tooltip";
import CalendarIcon from "../../../icons/CalendarIcon";
import HyroTextField from "../fields/text-field/HyroTextField";
import DateTimeContext from "../../../contexts/DateTimeContext";
import dayjs from "dayjs";

const DatePicker = ({
  value,
  minDate,
  shouldDisableDate,
  onChange,
  testId,
  disableFuture = true,
  fullWidth = false
}) => {
  const theme = useTheme();
  const { timeZone } = useContext(DateTimeContext);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Refocus the input when calendar opens, unfocus when it closes
  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      // Use setTimeout to ensure the calendar has fully opened
      setTimeout(() => {
        const inputElement = wrapperRef.current.querySelector("input");
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    } else if (!isOpen && wrapperRef.current) {
      // Unfocus the input when calendar closes
      setTimeout(() => {
        const inputElement = wrapperRef.current.querySelector("input");
        if (inputElement) {
          inputElement.blur();
        }
      }, 0);
    }
  }, [isOpen]);

  return (
    // Wrapper div to ensure the input is focused when the calendar opens
    <div ref={wrapperRef}>
      <Tooltip title={shouldDisableDate(value) ? "Invalid date range" : ""}>
        <DesktopDatePicker
          timezone={timeZone}
          value={value ? value.format("MM/DD/YYYY") : null}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          minDate={minDate}
          shouldDisableDate={shouldDisableDate}
          onChange={(value) => {
            if (!value) return;
            if (!value.isValid?.()) {
              onChange(value);
              return;
            }
            const timeString = value.format("YYYY-MM-DDTHH:mm:ss");
            const zoned = dayjs.tz(timeString, timeZone);
            onChange(zoned);
          }}
          InputProps={{ "data-testid": testId }}
          InputAdornmentProps={{
            sx: {
              [`& .${buttonBaseClasses.root}`]: {
                paddingRight: "12px",
                "&:hover": {
                  backgroundColor: "transparent"
                },
                [`& .${touchRippleClasses.root}`]: {
                  display: "none"
                }
              }
            }
          }}
          components={{
            OpenPickerIcon: () => <CalendarIcon fontSize="small" color={theme.palette.system[700]} />
          }}
          renderInput={params => (
            <HyroTextField
              {...params}
              sx={{
                maxWidth: fullWidth ? "100%" : 160,
                width: fullWidth ? "100%" : undefined,
                [`& .${outlinedInputClasses.root}`]: {
                  minHeight: 42,
                  height: 42
                }
              }}
              onClick={() => {
                setIsOpen(true);
              }}
            />
          )}
          inputFormat="MM/DD/YYYY"
          disableFuture={disableFuture}
          PopperProps={{
            placement: "bottom-start",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [-4, 2],
                },
              },
            ],
          }}
          PaperProps={{
            sx: {
              boxShadow: theme.shadows[4],
            },
          }}
          componentsProps={{
            leftArrowButton: { "data-testid": "date-picker-left-arrow" },
          }}
        />
      </Tooltip>
    </div>
  );
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: 160,
  [`& .${outlinedInputClasses.root}`]: {
    height: 42,
    padding: `0 ${theme.spacing(1.5)} 0 ${theme.spacing(0.5)}`
  },
  input: {
    ...theme.typography.body1,
    padding: `${theme.spacing()} 0 ${theme.spacing()} ${theme.spacing()}`
  }
}));

export default DatePicker;
